<template>
  <v-card flat class="pa-6">
    <h2 class="text-h5 primary-header sans-serif">
      {{ $t('form.import.sheetMapper.title') }}
    </h2>
    <v-card-subtitle>
      {{ $t('form.import.sheetMapper.subtitle') }}
    </v-card-subtitle>
    <v-card-text>
      <!-- Company Selection Section -->
      <div class="black--text text-subtitle-1 font-weight-medium mb-2">
        {{ $t('form.wizard.selectCompany.title') }}
      </div>
      <select-company-form
        v-model="selectedCompanyId"
        :create-action="true"
        :row="true"
        :disabled="hasCompanySheetMapped"
        @input="handleCompanyChange"
      />
      <span class="grey--text text--darken-1 mt-2 mb-4 d-block">
        {{ hasCompanySheetMapped ? $t('form.import.sheetMapper.companyFromImport') : $t('form.import.sheetMapper.companyFromSelection') }}
      </span>
      <v-divider class="mb-4" />
      <v-row class="font-weight-bold text-body-2 black--text mb-2">
        <v-col cols="2">{{ $t('form.import.sheetMapper.sheetName') }}</v-col>
        <v-col cols="1" class="text-center">{{ $t('form.import.sheetMapper.rows') }}</v-col>
        <v-col cols="2" class="text-center">{{ $t('form.import.sheetMapper.headerRow') }}</v-col>
        <v-col cols="6">{{ $t('form.import.sheetMapper.entityType') }}</v-col>
        <v-col cols="1"></v-col>
      </v-row>
      <v-divider />
      <template v-for="(mapping, index) in localMappings">
        <v-row
          :key="mapping.sheetName"
          align="center"
          dense
          class="py-2"
        >
          <v-col cols="2">{{ mapping.sheetName }}</v-col>
          <v-col cols="1" class="text-center">{{ getRowCount(mapping.sheetName) }}</v-col>
          <v-col cols="2" class="text-center">
            <v-text-field
              :value="getDisplayHeaderRow(index)"
              type="number"
              min="1"
              dense
              outlined
              hide-details
              class="header-row-input"
              @input="handleHeaderRowChange(index, $event)"
            />
          </v-col>
          <v-col cols="6">
            <v-row no-gutters>
              <v-col cols="12" md="6" class="pr-md-2">
                <v-select
                  :value="localMappings[index].entityType"
                  :items="translatedEntityTypeOptions"
                  :placeholder="$t('form.import.sheetMapper.selectType')"
                  clearable
                  dense
                  outlined
                  hide-details
                  @change="handleEntityTypeChange(index, $event)"
                />
              </v-col>
              <v-col v-if="localMappings[index].entityType === 'Individual'" cols="12" md="6" class="pl-md-2 mt-2 mt-md-0">
                <v-select
                  v-model="localMappings[index].teamId"
                  :items="teamOptions"
                  :label="$t('page.individuals.list.teams.title')"
                  item-text="label"
                  item-value="value"
                  dense
                  outlined
                  hide-details
                  clearable
                  @change="emitUpdate"
                />
              </v-col>
            </v-row>
          </v-col>
          <v-col cols="1" class="text-center">
            <v-tooltip v-if="sheetsNeedingRemap.has(mapping.sheetName)" bottom>
              <template #activator="{ on, attrs }">
                <v-icon
                  small
                  color="warning"
                  v-bind="attrs"
                  v-on="on"
                >
                  mdi-alert
                </v-icon>
              </template>
              <span>{{ $t('form.import.sheetMapper.remapWarningTooltip') }}</span>
            </v-tooltip>
          </v-col>
        </v-row>
        <v-divider :key="`divider-${mapping.sheetName}`" />
      </template>
    </v-card-text>
  </v-card>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import {
  BulkImportWizardParsedSheet,
  BulkImportWizardSheetMapping,
  ImportEntityType
} from '@betterboards/shared/types/Import'
import { CompanyTeamTypes } from '@/API'
import {
  cloneBulkImportWizardSheetMappings,
  detectEntityTypeFromSheetName
} from '@/helpers/import/fieldMapper'
import SelectCompanyForm from '@/components/forms/companies/SelectCompanyForm.vue'

interface TeamOption {
  label: string
  value: CompanyTeamTypes
}

export default Vue.extend({
  name: 'BulkImportSheetMapper',
  components: {
    SelectCompanyForm
  },
  props: {
    parsedSheets: {
      type: Array as PropType<BulkImportWizardParsedSheet[]>,
      required: true
    },
    sheetMappings: {
      type: Array as PropType<BulkImportWizardSheetMapping[]>,
      required: true
    }
  },
  data () {
    return {
      localMappings: cloneBulkImportWizardSheetMappings(this.sheetMappings),
      selectedCompanyId: null as string | null,
      hasCompanySheetMapped: false,
      manuallyCleared: new Set<number>(),
      initialHeaderRows: {} as Record<string, number>,
      initialEntityTypes: {} as Record<string, ImportEntityType | null>,
      sheetConfigInitialized: false
    }
  },
  computed: {
    selectedCompany (): any {
      return this.$store.state.Company.selectedCompany
    },
    translatedEntityTypeOptions (): Array<{ text: string, value: ImportEntityType }> {
      return [
        { text: this.$t('form.import.sheetMapper.entityTypes.Company') as string, value: 'Company' as ImportEntityType },
        { text: this.$t('form.import.sheetMapper.entityTypes.Individual') as string, value: 'Individual' as ImportEntityType },
        { text: this.$t('form.import.sheetMapper.entityTypes.Committee') as string, value: 'Committee' as ImportEntityType }
      ]
    },
    teamOptions (): TeamOption[] {
      return [
        { label: this.$t('global.companyTeams.Board') as string, value: CompanyTeamTypes.Board },
        { label: this.$t('global.companyTeams.Executive') as string, value: CompanyTeamTypes.Executive },
        { label: this.$t('global.companyTeams.Stakeholder') as string, value: CompanyTeamTypes.Stakeholder }
      ]
    },
    sheetsNeedingRemap (): Set<string> {
      const sheets = new Set<string>()
      for (const mapping of this.localMappings) {
        const initialRow = this.initialHeaderRows[mapping.sheetName]
        const currentRow = mapping.headerRow ?? 0
        const initialEntityType = this.initialEntityTypes[mapping.sheetName]
        const currentEntityType = mapping.entityType

        const existingMappingsCount = this.sheetMappings.find(
          m => m.sheetName === mapping.sheetName
        )?.fieldMappings?.length ?? 0
        const hasExistingMappings = existingMappingsCount > 0

        const headerRowChanged = initialRow !== undefined && currentRow !== initialRow
        const entityTypeChanged = initialEntityType !== undefined && currentEntityType !== initialEntityType

        if (
          this.sheetConfigInitialized &&
          hasExistingMappings &&
          (headerRowChanged || entityTypeChanged)
        ) {
          sheets.add(mapping.sheetName)
        }
      }
      return sheets
    },
    sheetsNeedingRemapList (): string[] {
      return Array.from(this.sheetsNeedingRemap)
    }
  },
  watch: {
    sheetMappings: {
      handler (newVal: BulkImportWizardSheetMapping[]) {
        this.localMappings = cloneBulkImportWizardSheetMappings(newVal)
        if (!this.sheetConfigInitialized) {
          this.initializeSheetConfig()
        }
        this.autoDetectEntityTypes()
        this.updateHasCompanySheetMapped()
      },
      deep: true,
      immediate: true
    },
    sheetsNeedingRemapList: {
      handler (sheets: string[]) {
        this.$emit('sheetsNeedingRemap', sheets)
      },
      immediate: true
    }
  },
  methods: {
    initializeSheetConfig (): void {
      for (const mapping of this.localMappings) {
        this.initialHeaderRows[mapping.sheetName] = mapping.headerRow ?? 0
        this.initialEntityTypes[mapping.sheetName] = mapping.entityType
      }
      this.sheetConfigInitialized = true
    },
    getRowCount (sheetName: string): number {
      const sheet = this.parsedSheets.find(s => s.name === sheetName)
      return sheet?.rows.length ?? 0
    },
    getDisplayHeaderRow (index: number): number {
      return (this.localMappings[index].headerRow ?? 0) + 1
    },
    handleHeaderRowChange (index: number, value: string): void {
      const numValue = parseInt(value, 10)
      if (isNaN(numValue) || numValue < 1) {
        return
      }
      const headerRow = numValue - 1
      this.localMappings[index].headerRow = headerRow
      this.$emit('headerRowChanged', {
        sheetName: this.localMappings[index].sheetName,
        headerRow
      })
      this.emitUpdate()
    },
    autoDetectEntityTypes (): void {
      let hasChanges = false
      for (let i = 0; i < this.localMappings.length; i++) {
        const mapping = this.localMappings[i]
        if (mapping.entityType === null && !this.manuallyCleared.has(i)) {
          const detectedType = detectEntityTypeFromSheetName(mapping.sheetName)
          if (detectedType) {
            mapping.entityType = detectedType
            if (detectedType === 'Individual') {
              mapping.teamId = CompanyTeamTypes.Board
            }
            hasChanges = true
          }
        }
      }
      if (hasChanges) {
        this.emitUpdate()
      }
    },
    updateHasCompanySheetMapped (): void {
      this.hasCompanySheetMapped = this.localMappings.some(m => m.entityType === 'Company')
    },
    handleEntityTypeChange (index: number, entityType: ImportEntityType | null): void {
      this.localMappings[index].entityType = entityType
      if (entityType === null) {
        this.manuallyCleared.add(index)
      } else {
        this.manuallyCleared.delete(index)
        if (entityType === 'Individual') {
          this.localMappings[index].teamId = CompanyTeamTypes.Board
        } else {
          this.localMappings[index].teamId = undefined
        }
      }
      this.updateHasCompanySheetMapped()
      this.emitUpdate()
    },
    emitUpdate (): void {
      this.$emit('sheetsConfigured', cloneBulkImportWizardSheetMappings(this.localMappings))
    },
    handleCompanyChange (companyId: string): void {
      this.selectedCompanyId = companyId
      this.$emit('companySelected', companyId)
    }
  },
  created () {
    if (this.selectedCompany?.id) {
      this.selectedCompanyId = this.selectedCompany.id
    }
  }
})
</script>

<style lang="scss" scoped>
.header-row-input {
  max-width: 80px;
  margin: 0 auto;
}
</style>
